import { IonAvatar, IonBadge, IonButton, IonContent, IonFooter, IonIcon } from '@ionic/react';
import { history } from '../utils/history';
import { usePathname } from '../hooks/usePathname';
import { useHasRequiredDevicePaymentPlan } from '../query/hasRequiredDevicePaymentPlan';
import { useCurrentUserQuery } from '../query/currentUserQuery';
import OCAuth from '../client/OCAuth';
import { useTranslation } from '../hooks/useTranslation';
import styles from './SideNav.module.css';

/* Sidebar contents for the split-pane menu (App.tsx). Subscribes to the shared
   history itself for the active state — same reason as DevicesTabBadge. */
const SideNav: React.FC = () => {
  const { t } = useTranslation('common');
  const { t: tComponents } = useTranslation('components');
  const pathname = usePathname();
  const hasRequiredPaymentPlan = useHasRequiredDevicePaymentPlan();
  const user = useCurrentUserQuery().data;

  const items = [
    {
      id: 'activity',
      path: '/events',
      icon: '/assets/activity/activity-icon.svg',
      label: t('navigation.activity'),
      active: pathname.startsWith('/events') || pathname.startsWith('/activity'),
    },
    {
      id: 'devices',
      path: '/devices',
      icon: '/assets/device/onlycat-device-icon.svg',
      label: t('navigation.devices'),
      active: pathname.startsWith('/devices'),
      badge: hasRequiredPaymentPlan,
    },
    {
      id: 'account',
      path: '/account',
      icon: '/assets/settings/account-settings-icon.svg',
      label: t('navigation.account'),
      active: pathname.startsWith('/account'),
    },
  ];

  const avatar = (
    <IonAvatar className={styles.avatar} aria-hidden="true">
      <img src={user?.avatarUrl ?? '/assets/account/default-avatar.svg'} alt="" referrerPolicy="no-referrer" />
    </IonAvatar>
  );

  return (
    <>
    <IonContent className={styles.menu}>
      <div className={styles.brand}>
        <img className={styles.brandMark} src="/assets/logo/logo-mark.svg" alt="" />
        <span className={styles.brandName}>OnlyCat</span>
      </div>
      <nav className={styles.nav}>
        {items.map((item) => (
          <button
            key={item.id}
            className={`${styles.item} ${item.active ? styles.active : ''}`}
            aria-current={item.active ? 'page' : undefined}
            onClick={() => {
              if (history.location.pathname !== item.path) {
                history.push(item.path);
              }
            }}
          >
            <IonIcon aria-hidden="true" src={item.icon} />
            <span className={styles.label}>{item.label}</span>
            {item.badge && <IonBadge className="payment-dot" color="danger" />}
          </button>
        ))}
      </nav>
    </IonContent>
    {/* Signed-in identity, pinned under the nav (wide-layouts design; the
        admin app's menu has the same row). The row opens the account page,
        where sign-out lives; signed out it offers login directly, as the
        account page does. */}
    <IonFooter className={styles.footer}>
      {user
        ? <button
            className={styles.userRow}
            onClick={() => {
              if (history.location.pathname !== '/account') {
                history.push('/account');
              }
            }}
          >
            {avatar}
            <span className={styles.userMeta}>
              <span className={styles.userName}>{user.name ?? user.email}</span>
              {user.name && user.email && <span className={styles.userEmail}>{user.email}</span>}
            </span>
          </button>
        : <div className={styles.userRow}>
            {/* no avatar: the placeholder buys nothing and the 248px pane
                needs the room for the label next to the button */}
            <span className={styles.userMeta}>
              <span className={styles.userName}>{tComponents('side_nav.not_signed_in')}</span>
            </span>
            <IonButton size="small" fill="outline" onClick={() => OCAuth.login()}>
              {tComponents('side_nav.sign_in')}
            </IonButton>
          </div>}
    </IonFooter>
    </>
  );
};

export default SideNav;
